import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  BuyInvestmentIcon,
  costFormater,
  List,
  SellInvestmentIcon,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE } from '@/common/constants';
import {
  MoneyTransferPayload,
  MoneyTransferStatus,
  MoneyTransferType,
} from '@/modules/investments/investments.types';
import colors from '@/styles/colors';

interface Props extends MoneyTransferPayload {}
const InvestmentsTransactionsItem: React.FC<Props> = ({ id, amount, date, status, type }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const isBuyMode = useMemo(() => {
    if (type === MoneyTransferType.IndividualDeposit) {
      return true;
    }
    return false;
  }, [type]);

  const formattedAmount = useMemo(() => {
    let currentAmount = amount < 0 ? amount * -1 : amount;
    if (!isBuyMode) {
      currentAmount = currentAmount * -1;
    }

    return costFormater(currentAmount, {
      isPrecision: true,
      prefix: isBuyMode ? '+$' : '$',
    });
  }, [amount, isBuyMode]);
  const formattedDate = useMemo(() => {
    if (date) {
      return date.format(DEFAULT_DATE_FORMAT);
    }
    return EMPTY_FIELD_VALUE;
  }, [date]);

  const statusNode = useMemo(() => {
    if (status === MoneyTransferStatus.Paid) {
      return (
        <Box direction="row" justify="end" align="center" data-testid={`money-transfer-item_${id}_status`}>
          <Text size="medium" weight={400}>
            {t('Completed')}
          </Text>
          <div>
            <Box
              width="10px"
              height="10px"
              margin={{ left: 'xs' }}
              background={colors.statusColors.paidActiveComplete}
              style={{ borderRadius: '50%' }}
            />
          </div>
        </Box>
      );
    }
    return (
      <Box direction="row" justify="end" align="center" data-testid={`money-transfer-item_${id}_status`}>
        <Text size="medium" weight={400}>
          {t('Processing')}
        </Text>
        <Box
          width="10px"
          height="10px"
          margin={{ left: 'xs' }}
          background={colors.statusColors.needsHelp}
          style={{ borderRadius: '50%' }}
        />
      </Box>
    );
  }, [id, status, t]);
  if (isMobile) {
    return (
      <List.Item testId={`money-transfer-item_${id}`} pad="spacing16" key={id}>
        <Box direction="row" align="center" fill>
          {isBuyMode ? <BuyInvestmentIcon /> : <SellInvestmentIcon />}
          <Box direction="column" align="start" margin={{ left: 'spacing12' }}>
            <List.Item.Text data-testid={`money-transfer-item_${id}_title`}>
              {isBuyMode ? t('Buy Investments') : t('Sell Investments')}
            </List.Item.Text>
            <List.Item.Text>{statusNode}</List.Item.Text>
          </Box>
        </Box>

        <div>
          <Box direction="column" align="end">
            <List.Item.Text
              weight={700}
              data-testid={`money-transfer-item_${id}_amount`}
              color={isBuyMode ? 'success' : 'investmentsLoss'}
            >
              {formattedAmount}
            </List.Item.Text>
            <List.Item.Text weight={400} color="textSecondary" data-testid={`money-transfer-item_${id}_date`}>
              {formattedDate}
            </List.Item.Text>
          </Box>
        </div>
      </List.Item>
    );
  }
  return (
    <List.Item testId={`money-transfer-item_${id}`} key={id}>
      <Box direction="row" align="center" style={{ flex: 30 }}>
        {isBuyMode ? <BuyInvestmentIcon /> : <SellInvestmentIcon />}

        <List.Item.Text data-testid={`money-transfer-item_${id}_title`} margin={{ left: 's' }}>
          {isBuyMode ? t('Buy Investments') : t('Sell Investments')}
        </List.Item.Text>
      </Box>
      <List.Item.Text
        flex={10}
        weight={700}
        data-testid={`money-transfer-item_${id}_amount`}
        color={isBuyMode ? 'success' : 'investmentsLoss'}
      >
        {formattedAmount}
      </List.Item.Text>
      <List.Item.Text flex={8} data-testid={`money-transfer-item_${id}_date`}>
        {formattedDate}
      </List.Item.Text>
      <List.Item.Text flex={20}>{statusNode}</List.Item.Text>
    </List.Item>
  );
};
export default InvestmentsTransactionsItem;
